import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Alert,
  Skeleton,
  Fade,
  Paper,
  Tooltip,
  LinearProgress,
  Pagination,
  Divider,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search,
  MoreVertical,
  Eye,
  Calendar,
  Building,
  MapPin,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  RefreshCw,
  Zap,
  Play,
  FastForward,
  Shield,
  Database,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

import SyncIcon from '@mui/icons-material/Sync';

import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../components/common/Notification';
import { certificatesService } from '../../services/certificates';
import CustomButton from '../../components/ui/CustomButton';
import ViewCertificateDialog from './ViewCerificateDialog';

const SyncCertificateCard = ({
  certificate,
  onView,
  onSyncStage1,
  onSyncStage2,
  isDarkMode,
  isLoading,
  sx, // Accept styling props from parent
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const { showNotification } = useNotification();

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSyncStage1 = async () => {
    setIsSyncing(true);
    try {
      await onSyncStage1(certificate);
      showNotification('Certificate synced to Stage 1 successfully', 'success');
    } catch (error) {
      showNotification('Failed to sync to Stage 1', 'error');
    } finally {
      setIsSyncing(false);
      handleMenuClose();
    }
  };

  const handleSyncStage2 = async () => {
    setIsSyncing(true);
    try {
      await onSyncStage2(certificate);
      showNotification('Certificate synced to Stage 2 successfully', 'success');
    } catch (error) {
      showNotification('Failed to sync to Stage 2', 'error');
    } finally {
      setIsSyncing(false);
      handleMenuClose();
    }
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  // FIXED: Utility function to truncate long text
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    // Remove line breaks and extra spaces
    const cleanText = text.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
    return cleanText.length > maxLength
      ? cleanText.substring(0, maxLength) + '...'
      : cleanText;
  };

  const isExpired =
    certificate.ExpiryDate && new Date(certificate.ExpiryDate) < new Date();
  const status = isExpired ? 'expired' : certificate.Status || 'pending';

  const canSyncStage2 = certificate.IsStage1Sync && !certificate.IsStage2Sync;
  const canSyncStage1 = !certificate.IsStage1Sync;

  // Define card styling based on sync status
  const getCardStyling = () => {
    if (certificate.IsStage1Sync && certificate.IsStage2Sync) {
      return {
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)'
          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.08) 100%)',
        border: '2px solid rgba(16, 185, 129, 0.3)',
        boxShadow: isDarkMode
          ? '0 8px 32px rgba(16, 185, 129, 0.2)'
          : '0 8px 32px rgba(16, 185, 129, 0.15)',
      };
    } else if (certificate.IsStage1Sync && !certificate.IsStage2Sync) {
      return {
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)'
          : 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(124, 58, 237, 0.08) 100%)',
        border: '2px solid rgba(139, 92, 246, 0.3)',
        boxShadow: isDarkMode
          ? '0 8px 32px rgba(139, 92, 246, 0.2)'
          : '0 8px 32px rgba(139, 92, 246, 0.15)',
      };
    } else {
      return {
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)'
          : 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.08) 100%)',
        border: '2px solid rgba(239, 68, 68, 0.3)',
        boxShadow: isDarkMode
          ? '0 8px 32px rgba(239, 68, 68, 0.2)'
          : '0 8px 32px rgba(239, 68, 68, 0.15)',
      };
    }
  };

  const getStatusChip = () => {
    if (certificate.IsStage1Sync && certificate.IsStage2Sync) {
      return {
        label: '✅ FULLY SYNCED',
        color: '#10b981',
        background: 'linear-gradient(135deg, #10b98120, #10b98110)',
        border: '1px solid #10b98130',
      };
    } else if (certificate.IsStage1Sync && !certificate.IsStage2Sync) {
      return {
        label: '🔄 STAGE 1 SYNCED',
        color: '#8b5cf6',
        background: 'linear-gradient(135deg, #8b5cf620, #8b5cf610)',
        border: '1px solid #8b5cf630',
      };
    } else {
      return {
        label: '⏳ UNSYNCED',
        color: '#ef4444',
        background: 'linear-gradient(135deg, #ef444420, #ef444410)',
        border: '1px solid #ef444430',
      };
    }
  };

  const cardStyling = getCardStyling();
  const statusChip = getStatusChip();

  return (
    <Fade in={true} timeout={300}>
      <Card
        sx={{
          // FIXED: Apply parent styling and override with consistent sizing
          ...sx,
          ...cardStyling,
          minHeight: 270,
          maxHeight: 270,
          maxWidth: 300,
          minWidth: 300,
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow:
              certificate.IsStage1Sync && certificate.IsStage2Sync
                ? isDarkMode
                  ? '0 20px 40px rgba(16, 185, 129, 0.4)'
                  : '0 20px 40px rgba(16, 185, 129, 0.3)'
                : certificate.IsStage1Sync && !certificate.IsStage2Sync
                  ? isDarkMode
                    ? '0 20px 40px rgba(139, 92, 246, 0.4)'
                    : '0 20px 40px rgba(139, 92, 246, 0.3)'
                  : isDarkMode
                    ? '0 20px 40px rgba(239, 68, 68, 0.4)'
                    : '0 20px 40px rgba(239, 68, 68, 0.3)',
          },
        }}
      >
        {/* Status Indicator Bar */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background:
              certificate.IsStage1Sync && certificate.IsStage2Sync
                ? 'linear-gradient(90deg, #10b981, #059669)'
                : certificate.IsStage1Sync && !certificate.IsStage2Sync
                  ? 'linear-gradient(90deg, #8b5cf6, #7c3aed)'
                  : 'linear-gradient(90deg, #ef4444, #dc2626)',
          }}
        />

        <CardContent
          sx={{
            p: 3,
            pt: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // FIXED: Prevent overflow
          }}
        >
          {/* Header with COC Number, Status and Actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              mb: 2,
              flexShrink: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {' '}
              {/* FIXED: minWidth 0 for text truncation */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                >
                  {certificate.CocNumber || `CERT-${certificate.Id}`}
                </Typography>

                {/* Status Chip */}
                <Chip
                  label={statusChip.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: statusChip.color,
                    background: statusChip.background,
                    border: statusChip.border,
                    flexShrink: 0,
                  }}
                />
                
                {/* Reissued Certificate Badge */}
                {certificate.IsReCertificate === true && (
                  <Chip
                    label="🔄 REISSUED"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#8b5cf6',
                      background: 'linear-gradient(135deg, #8b5cf620, #8b5cf610)',
                      border: '1px solid #8b5cf630',
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
              {/* FIXED: Truncated Importer Name with Tooltip */}
              <Tooltip
                title={certificate.ImporterName || 'Unknown Importer'}
                arrow
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {truncateText(
                    certificate.ImporterName || 'Unknown Importer',
                    30
                  )}
                </Typography>
              </Tooltip>
            </Box>

            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                flexShrink: 0,
                '&:hover': {
                  background: isDarkMode
                    ? 'rgba(148, 163, 184, 0.1)'
                    : 'rgba(148, 163, 184, 0.1)',
                },
              }}
              disabled={isLoading || isSyncing}
            >
              <MoreVertical size={16} />
            </IconButton>
          </Box>

          {/* FIXED: Certificate Details with proper truncation */}
          <Box sx={{ mt: 2, flex: 1, overflow: 'hidden' }}>
            {certificate.ImporterAddress && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <MapPin
                  size={14}
                  color={isDarkMode ? '#94a3b8' : '#64748b'}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Tooltip title={certificate.ImporterAddress} arrow>
                  <Typography
                    variant="body2"
                    sx={{
                      ml: 1,
                      color: isDarkMode ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncateText(certificate.ImporterAddress, 35)}
                  </Typography>
                </Tooltip>
              </Box>
            )}

            {certificate.ExporterName && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <Building
                  size={14}
                  color={isDarkMode ? '#94a3b8' : '#64748b'}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Tooltip title={certificate.ExporterName} arrow>
                  <Typography
                    variant="body2"
                    sx={{
                      ml: 1,
                      color: isDarkMode ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncateText(certificate.ExporterName, 35)}
                  </Typography>
                </Tooltip>
              </Box>
            )}

            {certificate.InvoiceValue && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DollarSign
                  size={14}
                  color={isDarkMode ? '#94a3b8' : '#64748b'}
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    ml: 1,
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatCurrency(
                    certificate.InvoiceValue,
                    certificate.InvoiceCurrency
                  )}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, opacity: 0.3 }} />

            {/* Sync Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexShrink: 0 }}>
              {canSyncStage1 && (
                <CustomButton
                  variant="gradient"
                  gradient="primary"
                  size="small"
                  onClick={handleSyncStage1}
                  disabled={isSyncing}
                  startIcon={
                    isSyncing ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <Play size={14} />
                    )
                  }
                  sx={{
                    height: 28,
                    fontSize: '0.7rem',
                    background:
                      'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Sync Stage 1
                </CustomButton>
              )}

              {canSyncStage2 && (
                <CustomButton
                  variant="gradient"
                  gradient="success"
                  size="small"
                  onClick={handleSyncStage2}
                  disabled={isSyncing}
                  startIcon={
                    isSyncing ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <FastForward size={14} />
                    )
                  }
                  sx={{
                    height: 28,
                    fontSize: '0.7rem',
                    background:
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Sync Stage 2
                </CustomButton>
              )}

              {certificate.IsStage1Sync && certificate.IsStage2Sync && (
                <Chip
                  label="Fully Synced"
                  size="small"
                  sx={{
                    height: 28,
                    fontSize: '0.7rem',
                    background: 'linear-gradient(135deg, #10b98130, #10b98120)',
                    color: '#10b981',
                    border: '1px solid #10b98140',
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>

            {/* FIXED: Footer section with flexShrink 0 to prevent squashing */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                mt: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Calendar
                  size={12}
                  color={isDarkMode ? '#64748b' : '#94a3b8'}
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    ml: 0.5,
                    color: isDarkMode ? '#64748b' : '#94a3b8',
                    fontSize: '0.7rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Issued: {formatDate(certificate.IssuanceDate)}
                </Typography>
              </Box>

              {certificate.ExpiryDate && (
                <Typography
                  variant="caption"
                  sx={{
                    color: isExpired
                      ? '#ef4444'
                      : isDarkMode
                        ? '#64748b'
                        : '#94a3b8',
                    fontSize: '0.7rem',
                    flexShrink: 0,
                  }}
                >
                  Expires: {formatDate(certificate.ExpiryDate)}
                </Typography>
              )}
            </Box>
          </Box>

          {(isLoading || isSyncing) && (
            <LinearProgress
              sx={{
                mt: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                flexShrink: 0,
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                },
              }}
            />
          )}
        </CardContent>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              borderRadius: 2,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              onView(certificate);
              handleMenuClose();
            }}
          >
            <Eye size={16} style={{ marginRight: 8 }} />
            View Details
          </MenuItem>
          {canSyncStage1 && (
            <MenuItem onClick={handleSyncStage1}>
              <Play size={16} style={{ marginRight: 8 }} />
              Sync to Stage 1
            </MenuItem>
          )}
          {canSyncStage2 && (
            <MenuItem onClick={handleSyncStage2}>
              <FastForward size={16} style={{ marginRight: 8 }} />
              Sync to Stage 2
            </MenuItem>
          )}
        </Menu>
      </Card>
    </Fade>
  );
};

const SyncCertificatesPage = () => {
  const { user, isAdmin, token } = useAuth();
  const { isDarkMode } = useTheme();
  const { showNotification, NotificationComponent } = useNotification();

  const [certificates, setCertificates] = useState([]);
  const [allCertificates, setAllCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncFilter, setSyncFilter] = useState('all');
  const [isReCertificateFilter, setIsReCertificateFilter] = useState(null); // null = all, true = reissued only, false = original only
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20); // User can now change this
  const [isAutoSyncing, setIsAutoSyncing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    totalUnsynced: 0,
    stage1Pending: 0,
    stage2Pending: 0,
    readyForStage2: 0,
  });

  // Fetch unsynced certificates
  const fetchUnSyncCertificates = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const allParams = {
        page: 1,
        pageSize: 10000,
        searchTerm: '',
        ...(isReCertificateFilter !== null && { isReCertificate: isReCertificateFilter }),
      };

      const allResponse =
        await certificatesService.getUnSyncCertificates(allParams);
      setAllCertificates(allResponse.data);

      const filtered = applyFilters(allResponse.data);

      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filtered.slice(startIndex, endIndex);

      setCertificates(paginatedData);
      setFilteredCertificates(filtered);
      setTotalCount(filtered.length);
      setTotalPages(Math.ceil(filtered.length / pageSize));
      setCurrentPage(page);

      // Fetch statistics
      const statsData = await certificatesService.getSyncStats();
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Failed to fetch unsynced certificates');
      showNotification('Failed to load certificates', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters to certificates
  const applyFilters = data => {
    let filtered = [...data];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        cert =>
          cert.CocNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cert.ImporterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cert.ExporterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cert.InvoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sync filter
    if (syncFilter !== 'all') {
      filtered = filtered.filter(cert => {
        switch (syncFilter) {
          case 'stage1-pending':
            return !cert.IsStage1Sync;
          case 'stage2-pending':
            return cert.IsStage1Sync && !cert.IsStage2Sync;
          case 'stage1-synced':
            return cert.IsStage1Sync && !cert.IsStage2Sync;
          case 'fully-synced':
            return cert.IsStage1Sync && cert.IsStage2Sync;
          default:
            return true;
        }
      });
    }

    // Reissue filter
    if (isReCertificateFilter !== null) {
      filtered = filtered.filter(cert => cert.IsReCertificate === isReCertificateFilter);
    }

    return filtered;
  };

  // Re-apply filters when filter criteria change
  useEffect(() => {
    if (allCertificates.length > 0) {
      const filtered = applyFilters(allCertificates);

      const startIndex = 0;
      const endIndex = pageSize;
      const paginatedData = filtered.slice(startIndex, endIndex);

      setCertificates(paginatedData);
      setFilteredCertificates(filtered);
      setTotalCount(filtered.length);
      setTotalPages(Math.ceil(filtered.length / pageSize));
      setCurrentPage(1);
    }
  }, [searchTerm, syncFilter, isReCertificateFilter, allCertificates, pageSize]);

  useEffect(() => {
    if (isAdmin()) {
      fetchUnSyncCertificates(1);
    }
  }, []);

  // FIXED: Enhanced pagination handlers from CertificatesManagementPage
  const handlePageChange = (event, newPage) => {
    const startIndex = (newPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = filteredCertificates.slice(startIndex, endIndex);

    setCertificates(paginatedData);
    setCurrentPage(newPage);
  };

  const handleFirstPage = () => {
    handlePageChange(null, 1);
  };

  const handleLastPage = () => {
    handlePageChange(null, totalPages);
  };

  const handlePreviousPage = () => {
    handlePageChange(null, Math.max(1, currentPage - 1));
  };

  const handleNextPage = () => {
    handlePageChange(null, Math.min(totalPages, currentPage + 1));
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle certificate actions
  const handleViewCertificate = certificate => {
    setSelectedCertificate(certificate);
    setViewDialogOpen(true);
  };

  const handleSyncStage1 = async certificate => {
    try {
      await certificatesService.syncToStage1(certificate.Id);
      fetchUnSyncCertificates(1);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleSyncStage2 = async certificate => {
    try {
      await certificatesService.syncToStage2(certificate.Id);
      fetchUnSyncCertificates(1);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };



  const handleAutoSync = async () => {
    setIsAutoSyncing(true);
    try {
      const result = await certificatesService.autoSyncToStage2();
      showNotification(
        `Auto-sync completed. ${result.syncedCount || 0} certificates synced to Stage 2`,
        'success'
      );
      fetchUnSyncCertificates(1);
    } catch (error) {
      showNotification('Auto-sync failed', 'error');
    } finally {
      setIsAutoSyncing(false);
    }
  };

  // Statistics cards data
  const statsCards = [
    {
      title: 'Total Unsynced',
      value: stats.totalUnsynced,
      icon: Database,
      color: '#3b82f6',
      change: stats.totalUnsynced > 0 ? 'Requires attention' : 'All synced',
    },
    {
      title: 'Stage 1 Pending',
      value: stats.stage1Pending,
      icon: Play,
      color: '#f59e0b',
      change: stats.stage1Pending > 0 ? 'Ready to sync' : 'All processed',
    },
    {
      title: 'Stage 2 Pending',
      value: stats.stage2Pending,
      icon: FastForward,
      color: '#8b5cf6',
      change: stats.stage2Pending > 0 ? 'Stage 1 completed' : 'All processed',
    },
    {
      title: 'Ready for Auto-Sync',
      value: stats.readyForStage2,
      icon: Zap,
      color: '#10b981',
      change: stats.readyForStage2 > 0 ? 'Can auto-sync' : 'None available',
    },
  ];

  // Redirect if not admin
  if (!isAdmin()) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Shield size={64} color="#ef4444" style={{ marginBottom: 16 }} />
        <Typography variant="h6" sx={{ color: '#ef4444', mb: 2 }}>
          Access Denied
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          This page is only accessible to administrators.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'transparent' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              textShadow: isDarkMode
                ? '0 0 20px rgba(59, 130, 246, 0.3)'
                : 'none',
            }}
          >
            Certificate Synchronization 🔄
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              fontSize: '1.1rem',
            }}
          >
            Manage certificate synchronization across different stages
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <CustomButton
            variant="outlined"
            onClick={() => fetchUnSyncCertificates(1)}
            disabled={isLoading}
            startIcon={<RefreshCw size={16} />}
            sx={{
              borderColor: isDarkMode ? '#374151' : '#d1d5db',
              color: isDarkMode ? '#e5e7eb' : '#374151',
              '&:hover': {
                borderColor: '#3b82f6',
                color: '#3b82f6',
                background: 'rgba(59, 130, 246, 0.1)',
              },
            }}
          >
            Refresh
          </CustomButton>

          <CustomButton
            variant="gradient"
            gradient="warning"
            onClick={handleAutoSync}
            disabled={isAutoSyncing || stats.readyForStage2 === 0}
            startIcon={
              isAutoSyncing ? (
                <RefreshCw size={16} className="animate-spin" />
              ) : (
                <Zap size={16} />
              )
            }
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                boxShadow: '0 8px 25px rgba(245, 158, 11, 0.6)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                opacity: 0.6,
                transform: 'none',
              },
            }}
          >
            {isAutoSyncing
              ? 'Auto-Syncing...'
              : `Auto-Sync Stage 2 (${stats.readyForStage2})`}
          </CustomButton>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <CustomButton
              size="small"
              onClick={() => fetchUnSyncCertificates(1)}
              startIcon={<RefreshCw size={14} />}
            >
              Retry
            </CustomButton>
          }
        >
          <Typography variant="body2">
            <strong>Error loading certificates:</strong> {error}
          </Typography>
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            {isLoading ? (
              <Card
                sx={{
                  height: 140,
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: isDarkMode
                    ? '1px solid rgba(148, 163, 184, 0.1)'
                    : '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Skeleton variant="text" width="60%" height={32} />
                      <Skeleton variant="text" width="80%" height={20} />
                    </Box>
                    <Skeleton variant="circular" width={48} height={48} />
                  </Box>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={16}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            ) : (
              <Fade in={true} timeout={300 + index * 100}>
                <div>
                  <Card
                    sx={{
                      height: 140,
                      background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: isDarkMode
                        ? '1px solid rgba(148, 163, 184, 0.1)'
                        : '1px solid rgba(148, 163, 184, 0.2)',
                      boxShadow: isDarkMode
                        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                        : '0 8px 32px rgba(0, 0, 0, 0.1)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: isDarkMode
                          ? `0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px ${stat.color}20`
                          : `0 20px 40px rgba(0, 0, 0, 0.15), 0 0 30px ${stat.color}20`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              color: stat.color,
                              mb: 0.5,
                              textShadow: `0 0 20px ${stat.color}40`,
                            }}
                          >
                            {stat.value}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? '#94a3b8' : '#64748b',
                              fontWeight: 500,
                            }}
                          >
                            {stat.title}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`,
                            border: `1px solid ${stat.color}30`,
                            boxShadow: `0 8px 16px ${stat.color}30`,
                          }}
                        >
                          {stat.icon && (
                            <stat.icon size={20} color={stat.color} />
                          )}
                        </Box>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? '#cbd5e1' : '#475569',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      >
                        {stat.change}
                      </Typography>
                    </CardContent>
                  </Card>
                </div>
              </Fade>
            )}
          </Grid>
        ))}
      </Grid>

      {/* Search and Filters */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search certificates by COC number, importer, or exporter..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      size={20}
                      color={isDarkMode ? '#94a3b8' : '#64748b'}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.7)'
                      : 'rgba(255, 255, 255, 0.7)',
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Sync Status</InputLabel>
              <Select
                value={syncFilter}
                onChange={e => setSyncFilter(e.target.value)}
                label="Sync Status"
                sx={{
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                }}
              >
                <MenuItem value="all">All Certificates</MenuItem>
                <MenuItem value="stage1-pending">Stage 1 Pending</MenuItem>
                <MenuItem value="stage2-pending">Stage 2 Pending</MenuItem>
                <MenuItem value="stage1-synced">Stage 1 Synced Only</MenuItem>
                <MenuItem value="fully-synced">Fully Synced</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Reissue Status</InputLabel>
              <Select
                value={isReCertificateFilter === null ? 'all' : isReCertificateFilter ? 'reissued' : 'original'}
                onChange={e => {
                  const value = e.target.value;
                  setIsReCertificateFilter(value === 'all' ? null : value === 'reissued');
                }}
                label="Reissue Status"
                sx={{
                  borderRadius: 2,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                }}
              >
                <MenuItem value="all">All Certificates</MenuItem>
                <MenuItem value="original">Original Only</MenuItem>
                <MenuItem value="reissued">🔄 Reissued Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Sync Legend">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: '#ef4444',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mr: 2 }}
                  >
                    Unsynced
                  </Typography>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: '#10b981',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                  >
                    Synced
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        {/* FIXED: Enhanced pagination info and controls from CertificatesManagementPage */}
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
              >
                Showing {certificates.length} of {totalCount} unsynced
                certificates
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? '#64748b' : '#94a3b8',
                  display: 'block',
                  mt: 0.5,
                }}
              >
                Page {currentPage} of {totalPages}
              </Typography>
            </Box>

            {/* Page Size Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  whiteSpace: 'nowrap',
                }}
              >
                Per page:
              </Typography>
              <FormControl size="small">
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  sx={{
                    minWidth: 70,
                    height: 32,
                    borderRadius: 1.5,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.5)',
                    '& .MuiSelect-select': {
                      py: 0.5,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: isDarkMode ? '#e2e8f0' : '#1e293b',
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* FIXED: Enhanced pagination controls from CertificatesManagementPage */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                padding: '8px 12px',
                borderRadius: 2,
                border: isDarkMode
                  ? '1px solid rgba(59, 130, 246, 0.2)'
                  : '1px solid rgba(59, 130, 246, 0.1)',
              }}
            >
              {/* First page button */}
              <IconButton
                onClick={handleFirstPage}
                disabled={currentPage === 1}
                size="small"
                sx={{
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.3)'
                      : 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <ChevronsLeft size={18} />
              </IconButton>

              {/* Previous page button */}
              <IconButton
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                size="small"
                sx={{
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.3)'
                      : 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <ChevronLeft size={18} />
              </IconButton>

              {/* Page numbers */}
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                hidePrevButton
                hideNextButton
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    fontWeight: 600,
                    minWidth: 36,
                    height: 36,
                    fontSize: '0.875rem',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    border: isDarkMode
                      ? '1px solid rgba(148, 163, 184, 0.2)'
                      : '1px solid rgba(148, 163, 184, 0.3)',
                    '&.Mui-selected': {
                      background:
                        'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      color: 'white',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                      fontWeight: 700,
                      '&:hover': {
                        background:
                          'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                      },
                    },
                    '&:hover': {
                      background: isDarkMode
                        ? 'rgba(59, 130, 246, 0.2)'
                        : 'rgba(59, 130, 246, 0.1)',
                      borderColor: '#3b82f6',
                      color: '#3b82f6',
                    },
                  },
                }}
              />

              {/* Next page button */}
              <IconButton
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                size="small"
                sx={{
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.3)'
                      : 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <ChevronRight size={18} />
              </IconButton>

              {/* Last page button */}
              <IconButton
                onClick={handleLastPage}
                disabled={currentPage === totalPages}
                size="small"
                sx={{
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.3)'
                      : 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <ChevronsRight size={18} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Certificates Grid */}
      {isLoading ? (
        <Grid container spacing={3}>
          {Array(Math.min(pageSize, 12))
            .fill({})
            .map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: 250,
                    background: isDarkMode
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: isDarkMode
                      ? '1px solid rgba(148, 163, 184, 0.1)'
                      : '1px solid rgba(148, 163, 184, 0.2)',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={24} />
                        <Skeleton variant="text" width="50%" height={20} />
                      </Box>
                    </Box>
                    {Array(6)
                      .fill({})
                      .map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="text"
                          width="90%"
                          height={20}
                          sx={{ mb: 1 }}
                        />
                      ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      ) : certificates.length > 0 ? (
        <Grid container spacing={3}>
          {certificates.map((certificate, index) => (
            <Grid item xs={12} sm={6} md={4} key={certificate.Id}>
              <SyncCertificateCard
                certificate={certificate}
                onView={handleViewCertificate}
                onSyncStage1={handleSyncStage1}
                onSyncStage2={handleSyncStage2}
                isDarkMode={isDarkMode}
                isLoading={isLoading}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SyncIcon
            size={64}
            color={isDarkMode ? '#64748b' : '#94a3b8'}
            style={{ marginBottom: 16 }}
          />
          <Typography
            variant="h6"
            sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mb: 1 }}
          >
            {searchTerm || syncFilter !== 'all'
              ? 'No certificates found'
              : 'All certificates are synced'}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', mb: 3 }}
          >
            {searchTerm || syncFilter !== 'all'
              ? 'Try adjusting your search filters'
              : 'Great! All certificates are synchronized across both stages'}
          </Typography>
        </Box>
      )}

      {/* View Certificate Dialog */}
      <ViewCertificateDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        certificate={selectedCertificate}
        isDarkMode={isDarkMode}
      />

      {/* Notification Component */}
      <NotificationComponent />
    </Box>
  );
};

export default SyncCertificatesPage;
